import { useMemo, type JSX } from "react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/shadcn/select";
import { Separator } from "../shadcn/separator";

interface IChipFilter {
  orderSelectOptions: {
    label: string;
    value: string;
    sort?: string;
    icon?: React.ReactNode;
    is_divider?: boolean;
  }[];
  selectedOrder: {
    label: string;
    value: string;
    sort?: string;
    icon?: React.ReactNode;
  };
  setSelectedOrder: (order: {
    label: string;
    value: string;
    sort?: string;
    icon?: React.ReactNode;
  }) => void;
  icon?: JSX.Element;
  labelIfFirstOption?: string;
  side?: "top" | "right" | "bottom" | "left";
  align?: "center" | "start" | "end";
  isDisabled?: boolean;
  testId?: string;
}

export const ChipFilter = ({
  orderSelectOptions,
  selectedOrder,
  setSelectedOrder,
  icon,
  labelIfFirstOption,
  isDisabled,
  side = "bottom",
  align = "start",
  testId,
}: IChipFilter) => {
  const title = useMemo(() => {
    if (orderSelectOptions.length === 0) {
      return selectedOrder.label;
    }
    return selectedOrder.value === orderSelectOptions[0].value &&
      labelIfFirstOption
      ? labelIfFirstOption
      : selectedOrder.label;
  }, [selectedOrder, labelIfFirstOption, orderSelectOptions]);

  const handleValueChange = (value: string) => {
    const selectedOption = orderSelectOptions.find(
      (option) => option.value === value
    );
    if (selectedOption && selectedOption.value !== selectedOrder.value) {
      setSelectedOrder({
        label: selectedOption.label,
        value: selectedOption.value,
        sort: selectedOption.sort,
        icon: selectedOption.icon,
      });
    }
  };

  return (
    <Select
      value={selectedOrder.value}
      onValueChange={handleValueChange}
      disabled={isDisabled}
    >
      <SelectTrigger
        className="!h-8"
        data-testid={testId}
        aria-label={labelIfFirstOption ?? title}
        style={{
          color:
            selectedOrder.value === "" || selectedOrder.value == undefined
              ? "var(--color-muted-foreground)"
              : "var(--color-foreground)",
        }}
      >
        <div className="flex items-center gap-2">
          {icon && <span className="flex-shrink-0">{icon}</span>}
          <SelectValue placeholder={title}>
            {selectedOrder.icon ? (
              <div className="flex items-center gap-2">
                <span className="flex-shrink-0">{selectedOrder.icon}</span>
                <span>{title}</span>
              </div>
            ) : (
              title
            )}
          </SelectValue>
        </div>
      </SelectTrigger>
      <SelectContent side={side} align={align}>
        {orderSelectOptions.map((option, index) =>
          option.is_divider ? (
            <Separator key={`${index}-separator`} />
          ) : (
            <SelectItem
              key={`${option.value}-${option.label}`}
              value={option.value}
              className="hover:bg-accent hover:text-accent-foreground transition-colors"
            >
              {option.icon ? (
                <div className="flex items-center gap-2">
                  <span className="flex-shrink-0">{option.icon}</span>
                  <span>{option.label}</span>
                </div>
              ) : (
                option.label
              )}
            </SelectItem>
          )
        )}
      </SelectContent>
    </Select>
  );
};
